import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Image } from "primereact/image";
import { Toast } from "primereact/toast";
import { ProjectBasicContact } from "../../../Controllers/Types/Project/ProjectInterface";
import {
  fetchServicesAndDefaulPackages,
  setCheckedContactForAssign,
} from "../../../Controllers/Slices/projectDetailsSlice";
import { useAppDispatch } from "../../../Controllers/hooks";
import { serviceShareContact } from "../../../Api/ProjectDetailsApi";
import { ShareContact } from "../../../Controllers/Types/Project/ShareContactInterface";
import { baseURL } from "../../../Api/ApiConstant";
import { useSelector } from "react-redux";
import { RootState } from "../../../Controllers/Store";
import { hasPermission } from "../../../Controllers/Selectors/permissionsSelector";
import { InputText } from "primereact/inputtext";

interface ContactAssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  toast: React.RefObject<Toast>;
  projectId: number;
  serviceId: number;
  projectContacts: ProjectBasicContact[];
  assignContacts: ShareContact[];
}

const ContactAssignModal: React.FC<ContactAssignModalProps> = ({
  isOpen,
  onClose,
  toast,
  projectId,
  serviceId,
  projectContacts,
  assignContacts,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [bodyHeight, setBodyHeight] = useState<number | "auto">("auto");
  const contactListBodyRef = useRef<HTMLDivElement>(null);
  const [userType, setUserType] = useState<"Employee" | "Professional" | "Client" | "All">("All");
  const [checkedContactList, setCheckedContactList] = useState<ShareContact[]>([]);
  const [filterContacts, setFilterContacts] = useState<ProjectBasicContact[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [contactSearchKey, setContactSearchKey] = useState<string>('');

  const updateHeight = () => {
    if (contactListBodyRef.current) {
      setBodyHeight(contactListBodyRef.current.offsetHeight - 170);
    }
  };

  useEffect(() => {
    if (isOpen && contactListBodyRef.current) {
      updateHeight();

      window.addEventListener("resize", updateHeight);

      return () => {
        window.removeEventListener("resize", updateHeight);
      };
    }
  }, [isOpen, contactListBodyRef.current]);

  const useCanEdit = (screen: string, permission: string) => {
    return useSelector((state: RootState) =>
      hasPermission(state, screen, permission)
    );
  };

  // check actions
  const addCheckList = (contact: ProjectBasicContact) => {
    const list = checkedContactList.filter((e) => e.id === contact.id);
    if (list && list.length > 0) {
      // need to remove list
      setCheckedContactList(
        checkedContactList.filter((e) => e.id !== contact.id)
      );
    } else {
      // need to add list
      setCheckedContactList([...checkedContactList, contact]);
    }
  };

  const isPermissionAddContactShortcut = useCanEdit("Project Details", "add_contact_shortcut");

  const handleAssignContact = async (e: React.FormEvent) => {
    e.preventDefault();
    const share: ShareContact[] = [];

    checkedContactList.forEach((contact) => {
      const shareCon: ShareContact = {
        by: contact.by,
        id: contact.id,
      };
      share.push(shareCon);
    });

    // if (share.length > 0) {
    setLoading(true);
    const res = await serviceShareContact(serviceId, share);
    setLoading(false);

    if (res.status) {
      setCheckedContactList([]);
      // dispatch(fetchAllServices(projectId))
      dispatch(fetchServicesAndDefaulPackages(projectId));
      onClose();

      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Shared successfully.",
          detail: "Folder shared successfully.",
          life: 3000,
        });
      }
    } else {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "There was an error sharing the folder.",
          life: 3000,
        });
      }
    }
    // } else {
    //   if (toast.current) {
    //     toast.current.show({
    //       severity: "error",
    //       summary: "Failed",
    //       detail: "Select at least one contact from the list to share with.",
    //       life: 3000,
    //     });
    //   }
    // }
  };



  // chcked as assign contacts
  useEffect(() => {
    if (isOpen) {
      setFilterContacts(projectContacts);
      handleSelectUserType('All');

      const shareBAsic: ShareContact[] = [];
      assignContacts.forEach((contact) => {
        const shareCon: ShareContact = {
          by: contact.by,
          id: contact.id,
        };
        shareBAsic.push(shareCon);
      });

      setCheckedContactList(shareBAsic);
    }
  }, [isOpen, assignContacts, projectContacts]);

  // filter actions.
  useEffect(() => {
    let filtered = projectContacts;

    if (userType === "Professional") {
      filtered = filtered.filter(
        (e) =>
          e.by.toLowerCase() === "company" ||
          e.by.toLowerCase() === "self employee"
      );
    } else if (userType === "Client") {
      filtered = filtered.filter(
        (e) =>
          e.by.toLowerCase() === "commercial client" ||
          e.by.toLowerCase() === "domestic client"
      );
    } else if (userType === "Employee") {
      filtered = filtered.filter(
        (e) => e.by.toLowerCase() === "employee"
      );
    }

    if (contactSearchKey.trim() !== "") {
      const search = contactSearchKey.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.first_name?.toLowerCase().includes(search) ||
          e.last_name?.toLowerCase().includes(search)
      );
    }

    setFilterContacts(filtered);
  }, [userType, contactSearchKey, projectContacts]);

  const getContactsCount = (type: "Employee" | "Professional" | "Client" | "All"): string => {
    if (!projectContacts) return "00";

    const typeMap: Record<string, string[]> = {
      Employee: ["employee"],
      Client: ["commercial client", "domestic client"],
      Professional: ["company", "self employee"],
    };

    let count: number;

    if (type === "All") {
      count = projectContacts.length;
    } else {
      const validTypes = typeMap[type];
      count = projectContacts.filter(e =>
        validTypes.includes(e.by.toLowerCase())
      ).length;
    }

    return String(count).padStart(2, "0");
  };

  /* Modal header */
  const assignContactModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">Share folder</h5>
      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={onClose}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  /* Modal footer */
  const assignContactModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={onClose}
      />

      {isPermissionAddContactShortcut && (
        <Button
          label={`${loading ? "Processing" : "Share"}`}
          loading={loading}
          onClick={handleAssignContact}
          className="custom_btn primary"
          disabled={assignContacts?.length === 0 && checkedContactList?.length === 0}
        />
      )}
    </div>
  );

  const handleSelectUserType = (
    type: "Employee" | "Professional" | "Client" | "All"
  ) => {
    setUserType(type);
  };

  return (
    <Dialog
      visible={isOpen}
      header={assignContactModalHeader}
      footer={assignContactModalFooter}
      headerClassName="custom_modal_header"
      className="custom_modal_dialog modal_dialog_sm assign_contact_modal"
      onHide={onClose}
      dismissableMask
    >
      <div className="custom_modal_body fixed_height" ref={contactListBodyRef}>
        <div className="pt-0 px-sm-0 px-1 pb-2 w-100">
          <div className="share_contact_tab_header">
            <div className="contact_tabs">
              <div className="contact_tabs">
                <div className="contact_tab_item">
                  <button
                    type="button"
                    className={`contact_tab_btn ${userType === "All" ? "active" : ""} p-ripple`}
                    onClick={() => handleSelectUserType('All')}
                  >
                    All: {getContactsCount('All')}
                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                  </button>
                </div>

                <div className="contact_tab_item">
                  <button
                    type="button"
                    className={`contact_tab_btn ${userType === "Employee" ? "active" : ""} p-ripple`}
                    onClick={() => handleSelectUserType("Employee")}
                  >
                    Employee: {getContactsCount('Employee')}
                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                  </button>
                </div>

                <div className="contact_tab_item">
                  <button
                    type="button"
                    className={`contact_tab_btn ${userType === "Professional" ? "active" : ""} p-ripple`}
                    onClick={() => handleSelectUserType("Professional")}
                  >
                    Professional: {getContactsCount('Professional')}
                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                  </button>
                </div>

                <div className="contact_tab_item">
                  <button
                    type="button"
                    className={`contact_tab_btn ${userType === "Client" ? "active" : ""} p-ripple`}
                    onClick={() => handleSelectUserType("Client")}
                  >
                    Client: {getContactsCount('Client')}
                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                  </button>
                </div>
              </div>
            </div>

            <div className="data_search_input_area contact_search">
              <InputText
                key={"contactSearchKey"}
                id="contactSearchKey"
                value={contactSearchKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setContactSearchKey(e.target.value)
                }
                className="custom_form_input data_search_input"
                placeholder="Search contact by name"
                autoComplete="off"
                autoFocus
                type={"search"}
              />
              {contactSearchKey === "" ? (
                <i className="bi bi-search"></i>
              ) : (
                <i
                  className="bi bi-x-lg data_search_input_clear"
                  onClick={() => setContactSearchKey('')}
                ></i>
              )}
            </div>
          </div>

          <div className="row">
            {filterContacts?.length > 0 ? (
              <div className="col-12">
                <ul className="assign_contact_list_area mt-sm-2 ps-sm-4 pe-sm-4 mb-sm-3 mb-2">
                  {filterContacts.map((contact) => (
                    <li className="assign_contact_list w-100">
                      <Checkbox
                        onChange={(e) => addCheckList(contact)}
                        checked={checkedContactList.filter((e) => e.id === contact.id).length > 0}
                        disabled={!isPermissionAddContactShortcut}
                      ></Checkbox>

                      <button
                        className={`contact_slide_tab assign_contact p-ripple ${checkedContactList.filter((e) => e.id === contact.id).length > 0 && "active"}`}
                        onClick={
                          isPermissionAddContactShortcut
                            ? () => {
                              dispatch(setCheckedContactForAssign(contact));
                              addCheckList(contact);
                            }
                            : undefined
                        }
                      >
                        <Image
                          className="contact_profile_img"
                          src={`${baseURL}${contact?.profile_image}`}
                          alt="Profile"
                        />
                        <div className="contact_slide_det">
                          <h6>
                            {contact.first_name + " " + contact?.last_name}
                          </h6>
                          {contact.role_name && (
                            <p>
                              {contact.role_name}
                            </p>
                          )}
                          {contact?.email && (
                            <small>
                              {contact?.email}
                            </small>
                          )}
                        </div>
                        <Ripple pt={{ root: { style: { background: "rgba(139, 96, 253, 0.2)" } } }} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="col-12">
                <div className="no_data_found d-flex align-content-center flex-column justify-content-center mt-2 mb-2"
                  style={{ height: bodyHeight }}>
                  <h6>
                    {contactSearchKey
                      ? `No contact(s) found matching "${contactSearchKey}".`
                      : (userType === "All" || projectContacts?.length === 0)
                        ? "No contact(s) are assigned to this project. Go back to Projects and assign contacts to this project."
                        : `No ${userType.toLowerCase()} contact(s) found!`}
                  </h6>

                  {projectContacts?.length === 0 && (
                    <button
                      className={`add_data_btn p-ripple mt-3 mx-auto`}
                      onClick={() => navigate('/project')}
                    >
                      <i className={`bi bi-box-arrow-up-left`}></i>
                      Projects
                      <Ripple />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ContactAssignModal;
